/**
 * The person's own address book as the portal's delivery picker uses it
 * (docs/features/customer-portal-ordering/21): which list the picker draws, and what a refused typed address says.
 *
 * Headless, and no business wording: every sentence shown is the backend's.
 */

import { ApiError } from "../data/api/errors";
import type {
  CustomerAddressBook,
  CustomerAddressBookEntry,
  CustomerAddressCountry,
  CustomerBasketRemovedAddress,
  CustomerPostcodeAddress,
  CustomerTypedAddressRequest,
} from "../data/customer-basket-types";
import type { CustomerDeliveryAddress } from "../data/customer-api-types";
import { extractFieldErrors } from "../data/validation";

/** Where the picker's addresses come from: the person's own book (once it is the rule), or the customer's own list. */
export type AddressPickerSource = "address_book" | "customer_list";

/** An address the picker offers: enough to name and read it, and (from the book) where its entry is changed or removed. */
export interface PickerAddress {
  address_id: string;
  name: string;
  /** The address's own lines. The customer's own list gives one string with the town and county already in it: it is split into lines here. */
  lines: string[];
  town: string | null;
  postcode: string;
  is_default: boolean;
  /** The book entry's `remove_url`: where "Remove from my address book" goes. Null for the customer's own list. */
  remove_url: string | null;
  /** The book entry's `update_url`: where *Make this my default address* and *Clear my default* go. Null for the customer's own list. */
  update_url: string | null;
}

export interface AddressPicker {
  source: AddressPickerSource;
  addresses: PickerAddress[];
  /** Where a typed address is posted; null while the customer's own list is the picker (nothing to add to). */
  add_url: string | null;
  /** Where a UK postcode is looked up for the add form; null where no address provider is set up (the form offers typing only). */
  postcode_lookup_url: string | null;
  /** The countries the add form's dropdown offers, as the server lists them (one per ISO country, United Kingdom first). */
  countries: CustomerAddressCountry[];
}

/**
 * Whether the book is the picker. The server's `enforced` is the whole rule: until it is on, the customer's own list is
 * still what is offered (and the book is not shown at all), so nothing changes for anyone before the switch.
 */
export function isBookThePicker(book: CustomerAddressBook | null): boolean {
  return book !== null && book.enforced;
}

function fromEntry(entry: CustomerAddressBookEntry): PickerAddress {
  return {
    address_id: entry.address_id,
    name: entry.name,
    lines: entry.lines,
    town: entry.town,
    postcode: entry.postcode ?? "",
    is_default: entry.is_default,
    remove_url: entry.remove_url,
    update_url: entry.update_url,
  };
}

/** The picker's list: the book's addresses when the book is the picker, else the customer's own list as it was. */
export function addressPickerFrom(
  book: CustomerAddressBook | null,
  customerList: CustomerDeliveryAddress[],
): AddressPicker {
  if (book !== null && book.enforced) {
    return {
      source: "address_book",
      addresses: book.addresses.map(fromEntry),
      add_url: book.add_url,
      postcode_lookup_url: book.postcode_lookup_url,
      countries: book.countries,
    };
  }
  return {
    source: "customer_list",
    addresses: customerList.map((a) => ({
      address_id: a.address_id,
      name: a.name,
      lines: a.address
        .split(",")
        .map((line) => line.trim())
        .filter(Boolean),
      town: null,
      postcode: a.postcode,
      is_default: a.is_default,
      remove_url: null,
      update_url: null,
    })),
    add_url: null,
    postcode_lookup_url: null,
    countries: [],
  };
}

/**
 * What *Add it again* posts for a basket line's removed address: the address as the server sent it, in the add route's own
 * fields (typing an address again brings back one the person removed). Null when it cannot be posted as it stands (no
 * lines, postcode or country), in which case the line offers removing the item only.
 */
export function addBackRequest(
  removed: CustomerBasketRemovedAddress,
): CustomerTypedAddressRequest | null {
  if (
    removed.add_back_url === null ||
    removed.lines.length === 0 ||
    !removed.postcode ||
    !removed.country_code
  )
    return null;
  return {
    label: removed.name,
    lines: removed.lines,
    town: removed.town,
    county: removed.county,
    postcode: removed.postcode,
    country_code: removed.country_code,
    recipient_name: removed.recipient_name,
    recipient_phone: removed.recipient_phone,
    delivery_instructions: removed.delivery_instructions,
  };
}

/** The country the add form starts on: the United Kingdom, which the server lists first. */
export const DEFAULT_ADDRESS_COUNTRY_CODE = "GB";

/**
 * How an address reads under its name: its lines and town on one line, without repeating the name (the book's name is the
 * address's first line until the person gives it one of their own). The postcode is shown apart from it.
 */
export function pickerAddressLine(address: PickerAddress): string {
  const [first, ...rest] = address.lines;
  const lines =
    first !== undefined && first.toLowerCase() === address.name.toLowerCase()
      ? rest
      : address.lines;
  return [...lines, address.town].filter(Boolean).join(", ");
}

/** The address a picker starts on: the default, else the first (the book lists the most recently used first). */
export function startingAddressId(addresses: PickerAddress[]): string | null {
  return (
    addresses.find((a) => a.is_default)?.address_id ??
    addresses[0]?.address_id ??
    null
  );
}

/** The inputs of the typed-address form, in the page's own field names. */
export const ADDRESS_FORM_FIELDS = [
  "label",
  "lines",
  "town",
  "county",
  "postcode",
  "country_code",
  "recipient_name",
  "recipient_phone",
  "delivery_instructions",
] as const;
export type AddressFormField = (typeof ADDRESS_FORM_FIELDS)[number];

export interface AddressFormFailure {
  /** The banner: a count when inputs are highlighted, else the server's own words. */
  summary: string;
  /** The backend's message for each input it refused, to show beside that input. */
  fieldErrors: Partial<Record<AddressFormField, string>>;
}

/**
 * What to show when a typed address is refused: each refusal bound to the input it names (`lines.0` is the
 * address-lines input) and counted in the banner; anything else, the server's own message.
 */
export function addressFormFailure(err: unknown): AddressFormFailure {
  const fieldErrors: AddressFormFailure["fieldErrors"] = {};
  const unbound: string[] = [];
  for (const e of extractFieldErrors(err)) {
    const input = e.field.split(".")[0] ?? e.field;
    if ((ADDRESS_FORM_FIELDS as readonly string[]).includes(input))
      fieldErrors[input as AddressFormField] ??= e.message;
    else unbound.push(e.message);
  }
  const count = Object.keys(fieldErrors).length;
  const counted =
    count === 0
      ? null
      : count === 1
        ? "One field needs correcting."
        : `${count} fields need correcting.`;
  if (counted || unbound.length > 0)
    return {
      summary: [...unbound, counted].filter(Boolean).join(" "),
      fieldErrors,
    };
  if (err instanceof ApiError && err.status === 429)
    return {
      summary:
        "You've added a lot of addresses just now. Wait a moment and try again.",
      fieldErrors,
    };
  return {
    summary:
      err instanceof ApiError && err.detail
        ? err.detail
        : "We couldn't add that address just now. Please try again.",
    fieldErrors,
  };
}

/**
 * What the form says when the server cannot be reached at all (offline, a dropped connection): the one sentence of
 * the postcode lookup that the server cannot supply. Every answer the server does give is shown in its own words.
 */
export const POSTCODE_LOOKUP_UNREACHABLE =
  "Postcode lookup didn't answer: type the address in.";

/**
 * Why *Find address* gave nothing to choose from, for the person to read: the server's own words where it gave some (a
 * field refusal for a postcode that is not a UK one, the 503 "type the address in", the wait after too many), else that
 * the lookup could not be reached. Typing the address in always stays open, so this is a note, never a block.
 */
export function postcodeLookupFailure(err: unknown): string {
  const fieldError = extractFieldErrors(err)[0];
  if (fieldError) return fieldError.message;
  if (err instanceof ApiError) {
    if (err.status === 429)
      return "You've searched a lot just now. Wait a moment, or type the address in.";
    if (err.status === 503 && err.detail) return err.detail;
  }
  return POSTCODE_LOOKUP_UNREACHABLE;
}

/** The form fields a chosen premise fills (the form's own names; the address's name only when the premise has an organisation). */
export interface PremiseFill {
  /** Offered as the address's name; the form uses it only if the person has not typed one. */
  name: string | null;
  line1: string;
  line2: string;
  line3: string;
  town: string;
  county: string;
  postcode: string;
  countryCode: string;
}

/** A premise the lookup found, as the add form's fields. */
export function fillFromPremise(premise: CustomerPostcodeAddress): PremiseFill {
  return {
    name: premise.organisation,
    line1: premise.lines[0] ?? "",
    line2: premise.lines[1] ?? "",
    line3: premise.lines[2] ?? "",
    town: premise.town ?? "",
    county: premise.county ?? "",
    postcode: premise.postcode,
    countryCode: premise.country_code,
  };
}
